import UmlPackageShape from '../Types/UmlPackageShape';
import UmlClassShape from '../Types/UmlClassShape';
import UmlCommentShape from '../Types/UmlCommentShape';
import UmlIconShape from '../Types/UmlIconShape';
import UmlSeqParticipantShape from '../Types/UmlSeqParticipantShape';
import UmlSeqLifelineShape from '../Types/UmlSeqLifelineShape';
import UmlSeqMessageShape from '../Types/UmlSeqMessageShape';
import UmlSeqActivationShape from '../Types/UmlSeqActivationShape';
import UmlCaption from '../Types/UmlCaption';
import UmlMemberText from '../Types/UmlMemberText';
import UmlConnector from '../Types/UmlConnector';
import UmlConnectorPoint from '../Types/UmlConnectorPoint';

/**
 * UmlShapeSerializer — reconstrueert echte UmlShape/UmlConnector-class-instanties uit plain
 * JSON-objecten (zoals JSON.parse(note.body) of jsonlint.parse() in de Body-tab teruggeven).
 *
 * WAAROM dit nodig is: `UmlCanvasEngine.toSchema()` serialiseert shapes/connectors gewoon via
 * JSON.stringify — dat bewaart alle eigen properties, maar GEEN class-identiteit. Terug
 * inladen via JSON.parse geeft dus plain objects, geen `UmlPackageShape`/`UmlClassShape`-
 * instanties. `UmlCanvas.jsx`'s renderShape() checkt `instanceof UmlPackageShape`/`UmlClassShape`
 * — een plain object faalt die check altijd, dus geladen shapes renderden stil niet (bug
 * gevonden 2026-07-09 bij Load Existing met een echte note).
 *
 * Discriminant: elke shape-class zet nu `this.kind` in de constructor ('package'/'class'/
 * 'comment', zie UmlPackageShape.js/UmlClassShape.js/UmlCommentShape.js). Notes die VOOR deze
 * fix zijn opgeslagen hebben dat veld niet — vandaar de structurele fallback in
 * `inferKind()` hieronder (op basis van welke velden aanwezig zijn), zodat oude data ook nog
 * inleest. Nieuwe saves hebben altijd een `kind`, dus de fallback is puur voor legacy-data.
 */

function inferKind(plain) {
  if (Array.isArray(plain.children)) return 'package';
  if (plain.caption !== undefined && Array.isArray(plain.members)) return 'class';
  if (plain.text !== undefined) return 'comment';
  return null;
}

function fromPlainCaption(plain) {
  if (!plain) return null;
  const caption = new UmlCaption(plain.id, plain.x, plain.y, plain.text, plain.stereotype);
  return caption;
}

function fromPlainMember(plain) {
  return new UmlMemberText(plain.id, plain.text, plain.visibility, plain.memberKind, plain.description, plain.paramCount, plain.smells, plain.name, plain.dependencies);
}

function fromPlainShape(plain) {
  if (!plain) return null;
  const kind = plain.kind || inferKind(plain);

  let shape;
  if (kind === 'package') {
    shape = new UmlPackageShape(plain.id, plain.x, plain.y, plain.w, plain.h, plain.name);
    (plain.children || []).forEach(function reconstructChild(childPlain) {
      // addChild zet zelf parentId + zIndex opnieuw — dat is prima, containment is
      // structureel al correct doordat childPlain in dit package's children[] stond.
      shape.addChild(fromPlainShape(childPlain));
    });
  } else if (kind === 'class') {
    shape = new UmlClassShape(plain.id, plain.x, plain.y, plain.w, plain.h, fromPlainCaption(plain.caption), plain.description, plain.inherits, plain.collapsed);
    (plain.members || []).forEach(function reconstructMember(memberPlain) {
      shape.addMember(fromPlainMember(memberPlain));
    });
  } else if (kind === 'comment') {
    shape = new UmlCommentShape(plain.id, plain.x, plain.y, plain.w, plain.h, plain.text);
    shape.linkedShapeId = plain.linkedShapeId ?? null;
  } else if (kind === 'icon') {
    // Geen inferKind-fallback nodig — dit is een nieuw shape-type (2026-07-12), er bestaat
    // geen legacy data zonder expliciet 'kind' veld om structureel te hoeven raden.
    shape = new UmlIconShape(plain.id, plain.x, plain.y, plain.w, plain.h, fromPlainCaption(plain.caption), plain.iconKind, plain.description);
  } else if (kind === 'seq-participant') {
    shape = new UmlSeqParticipantShape(plain.id, plain.x, plain.y, plain.w, plain.h, plain.name, plain.namespace, plain.linkedParticipantId, plain.isActor);
  } else if (kind === 'seq-lifeline') {
    shape = new UmlSeqLifelineShape(plain.id, plain.x, plain.y, plain.w, plain.h, plain.participantName, plain.participantId);
  } else if (kind === 'seq-message') {
    shape = new UmlSeqMessageShape(plain.id, plain.x, plain.y, plain.w, plain.h, plain.fromX, plain.toX, fromPlainCaption(plain.label), plain.boundary, plain.fromParticipantId, plain.toParticipantId, plain.isReturn, plain.pairedMessageId, plain.rowIndex, plain.params);
  } else if (kind === 'seq-activation') {
    shape = new UmlSeqActivationShape(plain.id, plain.x, plain.y, plain.w, plain.h, plain.participantId, plain.name ? plain.name.replace(' activation', '') : null, plain.startRowIndex, plain.endRowIndex);
  } else {
    return null; // onbekend/kapot shape-object — skip i.p.v. crashen op de rest van de load
  }

  // zIndex/parentId zijn geen constructor-args (zie UmlShape.js) — na constructie zetten
  // zodat de opgeslagen z-order/containment-status behouden blijft i.p.v. te resetten.
  shape.zIndex = plain.zIndex ?? shape.zIndex;
  shape.parentId = plain.parentId ?? null;
  // collapsed zit generiek op UmlShape (elke box-shape) — UmlClassShape zet 'm al via de
  // constructor hierboven, dit dekt Package/Comment/Icon en oude data zonder het veld.
  shape.collapsed = plain.collapsed ?? shape.collapsed;
  return shape;
}

function fromPlainConnector(plain) {
  if (!plain) return null;
  const connector = new UmlConnector(plain.id, plain.fromShapeId, plain.toShapeId, plain.kind);
  connector.style = plain.style ?? null;
  connector.fromAnchorPoint = plain.fromAnchorPoint ?? null;
  connector.toAnchorPoint = plain.toAnchorPoint ?? null;
  if (plain.label) {
    connector.setLabel(fromPlainCaption(plain.label));
  }
  (plain.points || []).forEach(function reconstructPoint(pointPlain) {
    connector.addPoint(new UmlConnectorPoint(pointPlain.id, pointPlain.x, pointPlain.y, pointPlain.connectorId));
  });
  return connector;
}

function fromPlainSchema(schemaData) {
  const shapes = (schemaData?.shapes || [])
    .map(fromPlainShape)
    .filter(Boolean);
  const connectors = (schemaData?.connectors || [])
    .map(fromPlainConnector)
    .filter(Boolean);
  return { shapes, connectors };
}

const umlShapeSerializer = { fromPlainShape, fromPlainConnector, fromPlainSchema };
export default umlShapeSerializer;
